import React, { useState, useEffect } from 'react';
import { 
  User as UserIcon, 
  Mail, 
  Globe, 
  Shield, 
  LogOut,
  Camera,
  Save,
  Languages,
  Check,
  Sparkles,
  Palette,
  LayoutGrid,
  CheckCircle2,
  ShieldCheck
} from 'lucide-react';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from '../firebase';
import { notify } from '../services/notify';
import { APP_LANGUAGE_STORAGE_KEY, normalizeLanguage, notifyLanguageChange } from '../lib/localization';

interface UserProfile {
  displayName: string;
  email: string;
  photoURL: string;
  language: 'en' | 'ar';
  layoutPreference: 'classic' | 'modern' | 'pastel-greenery' | 'cherry-blossom' | 'minimal' | 'technical' | 'brutalist';
  role?: string;
}

interface ProfileViewProps {
  orgId?: string;
  language?: 'en' | 'ar';
  onLayoutPreferenceChange?: (preference: string) => void;
  onLanguageChange?: (lang: 'en' | 'ar') => void;
  isSuperAdmin?: boolean;
  onNavigate?: (tab: string) => void;
}

export default function ProfileView({ orgId, language = 'en', onLayoutPreferenceChange, onLanguageChange, isSuperAdmin = false, onNavigate }: ProfileViewProps) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const appAuthToken = localStorage.getItem('app_auth_token');
        if (appAuthToken) {
          const response = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${appAuthToken}` }
          });
          if (response.ok) {
            const data = await response.json();
            setProfile({
              displayName: data.displayName || data.email || 'User',
              email: data.email || '',
              photoURL: data.photoURL || '',
              language: normalizeLanguage(data.language || language),
              layoutPreference: data.layoutPreference || 'classic',
              role: data.role || 'Member'
            });
            setLoading(false);
            return;
          }
        }

        if (!auth.currentUser) {
          setLoading(false);
          return;
        }

        const docRef = doc(db, 'users', auth.currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setProfile({
            displayName: data.displayName || auth.currentUser.displayName || 'User',
            email: auth.currentUser.email || '',
            photoURL: data.photoURL || auth.currentUser.photoURL || '',
            language: normalizeLanguage(data.language || language),
            layoutPreference: data.layoutPreference || 'classic',
            role: data.role || 'Member'
          });
        } else {
          setProfile({
            displayName: auth.currentUser.displayName || 'User',
            email: auth.currentUser.email || '',
            photoURL: auth.currentUser.photoURL || '',
            language,
            layoutPreference: 'classic',
            role: 'Member'
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        notify.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleLanguageSelect = (nextLanguage: 'en' | 'ar') => {
    if (!profile) return;
    setProfile({ ...profile, language: nextLanguage });
    localStorage.setItem(APP_LANGUAGE_STORAGE_KEY, nextLanguage);
    notifyLanguageChange();
    onLanguageChange?.(nextLanguage);
  };

  const handleSelectTheme = (pref: UserProfile['layoutPreference']) => {
    if (!profile) return;
    setProfile({ ...profile, layoutPreference: pref });
    onLayoutPreferenceChange?.(pref);
    const localUserJson = localStorage.getItem('user');
    if (localUserJson) {
      try {
        const parsed = JSON.parse(localUserJson);
        parsed.layoutPreference = pref;
        localStorage.setItem('user', JSON.stringify(parsed));
      } catch (e) {}
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    setSaving(true);
    try {
      const appAuthToken = localStorage.getItem('app_auth_token');
      let savedProfile = { ...profile };

      if (appAuthToken) {
        const response = await fetch('/api/auth/me', {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${appAuthToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            displayName: profile.displayName,
            photoURL: profile.photoURL,
            language: profile.language,
            layoutPreference: profile.layoutPreference
          })
        });

        if (!response.ok) {
          throw new Error(`Profile update failed with status ${response.status}`);
        }

        const updatedProfile = await response.json();
        savedProfile = {
          ...profile,
          displayName: updatedProfile.displayName ?? profile.displayName,
          email: updatedProfile.email ?? profile.email,
          photoURL: updatedProfile.photoURL ?? profile.photoURL,
          language: updatedProfile.language ?? profile.language,
          layoutPreference: updatedProfile.layoutPreference ?? profile.layoutPreference,
          role: updatedProfile.role ?? profile.role
        };
        setProfile(savedProfile);
      } else {
        if (!auth.currentUser) return;

        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          displayName: profile.displayName,
          language: profile.language,
          layoutPreference: profile.layoutPreference,
          updatedAt: serverTimestamp()
        });
      }

      // Sync local storage user object
      const localUserJson = localStorage.getItem('user');
      if (localUserJson) {
        try {
          const parsed = JSON.parse(localUserJson);
          parsed.layoutPreference = savedProfile.layoutPreference;
          parsed.language = savedProfile.language;
          parsed.displayName = savedProfile.displayName;
          localStorage.setItem('user', JSON.stringify(parsed));
        } catch (e) {}
      }

      if (onLayoutPreferenceChange) {
        onLayoutPreferenceChange(savedProfile.layoutPreference);
      }
      if (onLanguageChange) {
        onLanguageChange(savedProfile.language);
      }

      notify.confirm(savedProfile.language === 'ar' ? 'تم تحديث الملف الشخصي بنجاح' : 'Profile updated successfully');

      if (savedProfile.language === 'ar') {
        document.documentElement.dir = 'rtl';
        document.documentElement.lang = 'ar';
      } else {
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = 'en';
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      notify.error(profile.language === 'ar' ? 'تعذر تحديث الملف الشخصي' : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile) return null;

  const getLayoutStyles = () => {
    switch (profile.layoutPreference) {
      case 'modern':
        return {
          container: "p-8 max-w-5xl mx-auto space-y-8 font-sans",
          header: "flex items-center justify-between bg-white p-8 rounded-2xl shadow-sm border border-slate-100",
          card: "bg-white border border-slate-100 rounded-2xl p-8 shadow-sm",
          button: "bg-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-indigo-700 transition-all shadow-md shadow-indigo-200",
          input: "w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 outline-none transition-all",
          title: "text-4xl font-extrabold text-slate-900 tracking-tight",
          accent: "text-indigo-600",
          badge: "bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
        };
      case 'pastel-greenery':
        return {
          container: "p-8 max-w-5xl mx-auto space-y-8 font-sans",
          header: "flex items-center justify-between bg-[#99E2B4]/20 p-8 rounded-[2.5rem] border border-[#56AB91]/30 backdrop-blur-md shadow-lg",
          card: "bg-white/80 border border-[#56AB91]/20 rounded-[2.5rem] p-8 shadow-xl backdrop-blur-sm",
          button: "bg-[#14746F] text-white px-8 py-3 rounded-full font-bold hover:bg-[#036666] transition-all shadow-lg hover:translate-y-[-2px] active:translate-y-[0px] shadow-[#14746F]/20",
          input: "w-full px-5 py-4 border-2 border-[#88D4AB]/30 rounded-2xl bg-[#99E2B4]/5 focus:ring-4 focus:ring-[#99E2B4]/20 focus:border-[#56AB91] outline-none transition-all font-medium",
          title: "text-4xl font-bold text-[#036666] tracking-tight italic serif",
          accent: "text-[#469D89]",
          badge: "bg-[#99E2B4]/30 text-[#14746F] px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm"
        };
      case 'cherry-blossom':
        return {
          container: "p-8 max-w-5xl mx-auto space-y-8 font-sans",
          header: "flex items-center justify-between bg-[#FFF0F3] p-8 rounded-[3rem] border-4 border-double border-[#FFCCD5] shadow-xl",
          card: "bg-white border-2 border-[#FFCCD5] rounded-[3rem] p-8 shadow-lg shadow-[#FFCCD5]/20",
          button: "bg-[#A4133C] text-white px-8 py-3 rounded-full font-bold hover:bg-[#800F2F] transition-all shadow-lg shadow-[#A4133C]/30 uppercase tracking-widest text-[10px]",
          input: "w-full px-6 py-4 border-2 border-[#FFCCD5] rounded-full bg-white focus:ring-4 focus:ring-[#FFF0F3] focus:border-[#A4133C] outline-none transition-all font-bold text-[#590D22] placeholder:text-[#590D22]/30",
          title: "text-4xl font-black text-[#590D22] tracking-tighter uppercase italic serif",
          accent: "text-[#FF4D6D]",
          badge: "bg-[#FFF0F3] text-[#A4133C] px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-[#FFCCD5]"
        };
      default: // classic
        return {
          container: "p-8 max-w-4xl mx-auto space-y-8",
          header: "flex items-center justify-between",
          card: "bg-card border rounded-xl p-6",
          button: "bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity",
          input: "w-full px-3 py-2 border rounded-lg bg-background focus:ring-2 focus:ring-primary/20 outline-none",
          title: "text-3xl font-bold tracking-tight",
          accent: "text-primary",
          badge: "text-sm text-muted-foreground"
        };
    }
  };

  const styles = getLayoutStyles();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={cn(styles.title, "flex items-center gap-4")}>
            <UserIcon className="w-10 h-10" />
            {profile.language === 'ar' ? 'الملف الشخصي' : 'User Profile'}
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            {profile.language === 'ar' ? 'إدارة معلومات حسابك وتفضيلاتك.' : 'Manage your account information and preferences.'}
          </p>
        </div>
        <button
          onClick={handleSaveProfile}
          disabled={saving}
          className={cn(styles.button, "disabled:opacity-50 mt-4 sm:mt-0")}
        >
          <Save className="w-5 h-5" />
          {saving ? (profile.language === 'ar' ? 'جاري الحفظ...' : 'Saving...') : (profile.language === 'ar' ? 'حفظ التغييرات' : 'Save Changes')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Profile Card */}
        <div className="md:col-span-1 space-y-6">
          <div className={cn(styles.card, "flex flex-col items-center text-center space-y-6")}>
            <div className="relative">
              <div className={cn(
                "w-40 h-40 rounded-full overflow-hidden border-4 flex items-center justify-center border-muted bg-muted"
              )}>
                {profile.photoURL ? (
                  <img src={profile.photoURL} alt={profile.displayName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                ) : (
                  <UserIcon className="w-20 h-20 text-muted-foreground" />
                )}
              </div>
              <button className={cn(
                "absolute bottom-2 right-2 p-3 rounded-full shadow-lg hover:opacity-90 transition-opacity bg-primary text-primary-foreground"
              )}>
                <Camera className="w-5 h-5" />
              </button>
            </div>
            <div>
              <h2 className="text-2xl font-bold">{profile.displayName}</h2>
              <div className="mt-2">
                <span className={styles.badge}>{profile.role}</span>
              </div>
            </div>
            <div className="w-full pt-6 border-t space-y-3">
              <div className="flex items-center justify-center gap-3 text-sm text-muted-foreground">
                <Mail className="w-5 h-5" />
                {profile.email}
              </div>
            </div>
          </div>

          <button
            onClick={() => auth.signOut()}
            className={cn(
              "w-full flex items-center justify-center gap-2 px-6 py-3 font-bold transition-all border border-destructive/20 text-destructive hover:bg-destructive/10 rounded-xl"
            )}
          >
            <LogOut className="w-5 h-5" />
            {profile.language === 'ar' ? 'تسجيل الخروج' : 'Sign Out'}
          </button>

          {isSuperAdmin && (
            <button
              onClick={() => onNavigate?.('super_admin')}
              className={cn(
                "w-full flex items-center justify-center gap-2 px-6 py-3 font-bold transition-all border border-primary/20 text-primary hover:bg-primary/10 rounded-xl"
              )}
            >
              <ShieldCheck className="w-5 h-5" />
              {profile.language === 'ar' ? 'لوحة تحكم المشرف العام' : 'Super Admin Panel'}
            </button>
          )}
        </div>

        {/* Settings Form */}
        <div className="md:col-span-2 space-y-8">
          <div className={cn(styles.card, "overflow-hidden p-0")}>
            <div className="p-6 border-b bg-muted/30">
              <h2 className="text-xl font-bold flex items-center gap-3">
                <Shield className="w-6 h-6" />
                {profile.language === 'ar' ? 'المعلومات الشخصية' : 'Personal Information'}
              </h2>
            </div>
            <div className="p-8 space-y-6">
              <div className="space-y-3">
                <label className="text-sm font-bold uppercase tracking-wider">
                  {profile.language === 'ar' ? 'الاسم الكامل' : 'Full Name'}
                </label>
                <input
                  type="text"
                  value={profile.displayName}
                  onChange={(e) => setProfile({ ...profile, displayName: e.target.value })}
                  className={styles.input}
                  dir={profile.language === 'ar' ? 'rtl' : 'ltr'}
                />
              </div>
              <div className="space-y-3">
                <label className="text-sm font-bold uppercase tracking-wider">
                  {profile.language === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}
                </label>
                <input
                  type="email"
                  value={profile.email}
                  disabled
                  className={cn(styles.input, "bg-muted text-muted-foreground cursor-not-allowed border-dashed")}
                />
                <p className="text-xs text-muted-foreground italic">
                  {profile.language === 'ar' ? 'لا يمكن تغيير البريد الإلكتروني المرتبط بحسابك.' : 'Email address cannot be changed as it is linked to your account.'}
                </p>
              </div>
            </div>
          </div>

          <div className={cn(styles.card, "overflow-hidden p-0")}>
            <div className="p-6 border-b bg-muted/30">
              <h2 className="text-xl font-bold flex items-center gap-3">
                <Languages className="w-6 h-6" />
                {profile.language === 'ar' ? 'تفضيلات اللغة' : 'Language Preferences'}
              </h2>
            </div>
            <div className="p-8 space-y-6">
              <div className="space-y-4">
                <label className="text-sm font-bold uppercase tracking-wider">
                  {profile.language === 'ar' ? 'لغة العرض' : 'Display Language'}
                </label>
                <div className="grid grid-cols-2 gap-6">
                  <button
                    onClick={() => handleLanguageSelect('en')}
                    className={cn(
                      "flex items-center justify-center gap-3 p-6 border-2 transition-all",
                      profile.language === 'en' 
                        ? "border-primary bg-primary/5 ring-4 ring-primary/10"
                        : "hover:bg-muted border-transparent"
                    )}
                  >
                    <span className="text-3xl">🇺🇸</span>
                    <div className="text-start">
                      <p className="font-bold">English</p>
                      <p className="text-[10px] uppercase text-muted-foreground">Default</p>
                    </div>
                  </button>
                  <button
                    onClick={() => handleLanguageSelect('ar')}
                    className={cn(
                      "flex items-center justify-center gap-3 p-6 border-2 transition-all",
                      profile.language === 'ar' 
                        ? "border-primary bg-primary/5 ring-4 ring-primary/10"
                        : "hover:bg-muted border-transparent"
                    )}
                  >
                    <span className="text-3xl">🇸🇦</span>
                    <div className="text-start">
                      <p className="font-bold">العربية</p>
                      <p className="text-[10px] uppercase text-muted-foreground">اللغة العربية</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Width Theme & Layout Preferences Section */}
      <div className="mt-8">
        <div className={cn(styles.card, "overflow-hidden p-0 border border-slate-200/80 dark:border-slate-800 shadow-sm")}>
          <div className="p-6 border-b border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-3">
                <Palette className="w-5 h-5 text-primary" />
                {profile.language === 'ar' ? 'تفضيلات المظهر والسمات' : 'Theme & Layout Preferences'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {profile.language === 'ar' ? 'اختر مظهر الواجهة ونسق الألوان المفضل لديك.' : 'Choose your preferred dashboard aesthetic and color scheme.'}
              </p>
            </div>
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-primary/10 text-primary border border-primary/20">
              <Sparkles className="w-3.5 h-3.5" />
              {profile.language === 'ar' ? 'تحديث فوري' : 'Live Sync'}
            </span>
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[
                {
                  id: 'classic' as const,
                  name: { en: 'Classic Slate', ar: 'كلاسيكي سليت' },
                  badge: { en: 'Enterprise', ar: 'مؤسسي' },
                  desc: { en: 'Slate sidebar & sky blue accents', ar: 'شريط جانبي كلاسيكي باللون الأزرق' },
                  colors: ['#1e293b', '#0284c7', '#f8fafc'],
                  sidebarBg: 'bg-slate-900',
                  headerBg: 'bg-white border-b border-slate-200',
                  bodyBg: 'bg-slate-100',
                  cardBg: 'bg-white border border-slate-200',
                  accentBg: 'bg-sky-600',
                  barColor: 'bg-slate-300'
                },
                {
                  id: 'modern' as const,
                  name: { en: 'Modern Indigo', ar: 'نيلي عصري' },
                  badge: { en: 'Recommended', ar: 'موصى به' },
                  desc: { en: 'Clean glass card layout', ar: 'تنسيق أنيق باللون النيلي' },
                  colors: ['#4f46e5', '#818cf8', '#e0e7ff'],
                  sidebarBg: 'bg-indigo-950',
                  headerBg: 'bg-white border-b border-indigo-100',
                  bodyBg: 'bg-indigo-50/50',
                  cardBg: 'bg-white border border-indigo-100',
                  accentBg: 'bg-indigo-600',
                  barColor: 'bg-indigo-200'
                },
                {
                  id: 'pastel-greenery' as const,
                  name: { en: 'Pastel Greenery', ar: 'خضار هادئ' },
                  badge: { en: 'Organic', ar: 'طبيعي' },
                  desc: { en: 'Sage green & forest mint', ar: 'ألوان النعناع والأخضر المهدئة' },
                  colors: ['#14746f', '#56ab91', '#e8f3e8'],
                  sidebarBg: 'bg-[#14746f]',
                  headerBg: 'bg-[#f2f7f2] border-b border-[#56ab91]/30',
                  bodyBg: 'bg-[#f8fbf8]',
                  cardBg: 'bg-white border border-[#56ab91]/30',
                  accentBg: 'bg-[#56ab91]',
                  barColor: 'bg-[#56ab91]/40'
                },
                {
                  id: 'cherry-blossom' as const,
                  name: { en: 'Cherry Blossom', ar: 'زهر الكرز' },
                  badge: { en: 'Vibrant', ar: 'حيوية' },
                  desc: { en: 'Sakura rose & fuchsia', ar: 'وردي زهر الكرز الأنيق' },
                  colors: ['#a4133c', '#ff4d6d', '#fff0f3'],
                  sidebarBg: 'bg-[#590d22]',
                  headerBg: 'bg-white border-b border-[#ffccd5]',
                  bodyBg: 'bg-[#fff5f7]',
                  cardBg: 'bg-white border border-[#ffccd5]',
                  accentBg: 'bg-[#a4133c]',
                  barColor: 'bg-[#ffccd5]'
                },
                {
                  id: 'minimal' as const,
                  name: { en: 'Monochrome Minimal', ar: 'بسيط أحادي' },
                  badge: { en: 'Sleek', ar: 'أنق' },
                  desc: { en: 'High precision neutral theme', ar: 'تصميم بسيط ومحايد ذو دقة عالية' },
                  colors: ['#171717', '#737373', '#f5f5f5'],
                  sidebarBg: 'bg-neutral-900',
                  headerBg: 'bg-white border-b border-neutral-200',
                  bodyBg: 'bg-neutral-100',
                  cardBg: 'bg-white border border-neutral-200',
                  accentBg: 'bg-neutral-900',
                  barColor: 'bg-neutral-300'
                },
                {
                  id: 'technical' as const,
                  name: { en: 'Technical Dark', ar: 'تقني داكن' },
                  badge: { en: 'Developer', ar: 'مطور' },
                  desc: { en: 'Dark workspace & neon green', ar: 'واجهة داكنة عالية الكثافة مع أخضر نيون' },
                  colors: ['#09090b', '#22c55e', '#27272a'],
                  sidebarBg: 'bg-black border-r border-zinc-800',
                  headerBg: 'bg-zinc-900 border-b border-zinc-800',
                  bodyBg: 'bg-zinc-950',
                  cardBg: 'bg-zinc-900 border border-zinc-800',
                  accentBg: 'bg-green-500',
                  barColor: 'bg-zinc-700'
                },
                {
                  id: 'brutalist' as const,
                  name: { en: 'Neo Brutalist', ar: 'بروتاليست' },
                  badge: { en: 'High Contrast', ar: 'تباين عالي' },
                  desc: { en: 'Bold black borders & pop yellow', ar: 'تباين عالي وحدود سوداء بارزة' },
                  colors: ['#fde047', '#000000', '#ffffff'],
                  sidebarBg: 'bg-yellow-300 border-r-2 border-black',
                  headerBg: 'bg-white border-b-2 border-black',
                  bodyBg: 'bg-amber-50',
                  cardBg: 'bg-white border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]',
                  accentBg: 'bg-black',
                  barColor: 'bg-black',
                  isBrutalist: true
                }
              ].map((theme) => {
                const isSelected = profile.layoutPreference === theme.id;
                const isAr = profile.language === 'ar';

                return (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => handleSelectTheme(theme.id)}
                    className={cn(
                      "relative flex flex-col text-left transition-all duration-200 rounded-2xl p-4.5 group outline-none cursor-pointer",
                      theme.isBrutalist 
                        ? isSelected
                          ? "bg-yellow-300 border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
                          : "bg-white border-2 border-black hover:bg-yellow-50 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
                        : isSelected
                          ? "bg-white dark:bg-slate-900 border-2 border-primary shadow-md ring-4 ring-primary/15"
                          : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm hover:scale-[1.01]"
                    )}
                  >
                    {/* Active Indicator Badge */}
                    {isSelected && (
                      <div className={cn(
                        "absolute top-3.5 right-3.5 z-10 w-6 h-6 rounded-full flex items-center justify-center shadow-sm",
                        theme.isBrutalist ? "bg-black text-yellow-300 border border-black" : "bg-primary text-primary-foreground"
                      )}>
                        <Check className="w-3.5 h-3.5 stroke-[3]" />
                      </div>
                    )}

                    {/* Miniature Dashboard Layout Wireframe Preview */}
                    <div className={cn(
                      "relative w-full h-32 rounded-xl overflow-hidden flex border transition-all mb-4",
                      theme.isBrutalist ? "border-2 border-black rounded-none shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]" : "border-slate-200/80 dark:border-slate-800"
                    )}>
                      {/* Mini Left Sidebar */}
                      <div className={cn("w-1/4 h-full p-2 flex flex-col justify-between shrink-0", theme.sidebarBg)}>
                        <div className="space-y-2">
                          <div className={cn("w-3 h-3 rounded-full mb-2.5", theme.isBrutalist ? "bg-black" : "bg-white/80")} />
                          <div className={cn("w-full h-1.5 rounded-full opacity-80", theme.isBrutalist ? "bg-black" : "bg-white/60")} />
                          <div className={cn("w-3/4 h-1.5 rounded-full opacity-50", theme.isBrutalist ? "bg-black" : "bg-white/40")} />
                          <div className={cn("w-4/5 h-1.5 rounded-full opacity-50", theme.isBrutalist ? "bg-black" : "bg-white/40")} />
                        </div>
                        <div className={cn("w-full h-1.5 rounded-full opacity-40", theme.isBrutalist ? "bg-black" : "bg-white/30")} />
                      </div>

                      {/* Mini Main Workspace Area */}
                      <div className={cn("flex-1 h-full flex flex-col min-w-0", theme.bodyBg)}>
                        {/* Mini Top Navbar */}
                        <div className={cn("h-6 w-full flex items-center justify-between px-2.5 shrink-0", theme.headerBg)}>
                          <div className={cn("w-12 h-1.5 rounded-full", theme.barColor)} />
                          <div className={cn("w-3 h-3 rounded-full shrink-0", theme.accentBg)} />
                        </div>

                        {/* Mini Workspace Content Widgets */}
                        <div className="p-2 space-y-2 flex-1 overflow-hidden">
                          <div className="grid grid-cols-2 gap-1.5">
                            <div className={cn("p-1.5 rounded-md flex flex-col justify-between h-8", theme.cardBg)}>
                              <div className={cn("w-4 h-1 rounded-full", theme.barColor)} />
                              <div className={cn("w-8 h-1.5 rounded-full", theme.accentBg)} />
                            </div>
                            <div className={cn("p-1.5 rounded-md flex flex-col justify-between h-8", theme.cardBg)}>
                              <div className={cn("w-5 h-1 rounded-full", theme.barColor)} />
                              <div className={cn("w-6 h-1.5 rounded-full", theme.barColor)} />
                            </div>
                          </div>
                          <div className={cn("p-1.5 rounded-md flex items-center justify-between h-7", theme.cardBg)}>
                            <div className="space-y-1 w-full">
                              <div className={cn("w-3/4 h-1.5 rounded-full", theme.barColor)} />
                              <div className={cn("w-1/2 h-1 rounded-full opacity-60", theme.barColor)} />
                            </div>
                            <div className={cn("w-2.5 h-2.5 rounded-full shrink-0 ml-1.5", theme.accentBg)} />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Theme Title & Badge Header */}
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className={cn(
                        "font-bold text-sm truncate",
                        theme.isBrutalist ? "font-mono text-black" : "text-foreground"
                      )}>
                        {isAr ? theme.name.ar : theme.name.en}
                      </span>
                      <span className={cn(
                        "text-[10px] font-semibold px-2.5 py-0.5 rounded-full shrink-0",
                        theme.isBrutalist 
                          ? "bg-black text-yellow-300 font-mono" 
                          : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                      )}>
                        {isAr ? theme.badge.ar : theme.badge.en}
                      </span>
                    </div>

                    {/* Subtitle Description */}
                    <p className="text-xs text-muted-foreground line-clamp-1 mb-3">
                      {isAr ? theme.desc.ar : theme.desc.en}
                    </p>

                    {/* Color Swatch Dots Footer */}
                    <div className="flex items-center gap-1.5 mt-auto pt-2.5 border-t border-slate-100 dark:border-slate-800/80">
                      <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider mr-1">
                        {isAr ? 'الألوان:' : 'Palette:'}
                      </span>
                      {theme.colors.map((color, idx) => (
                        <span
                          key={idx}
                          className="w-3 h-3 rounded-full border border-black/10 dark:border-white/20 shadow-xs"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper function for conditional classes
function cn(...inputs: any[]) {
  return inputs.filter(Boolean).join(' ');
}
